import { inject, Injectable } from '@angular/core';
import { emobilityActions } from '@innogy/emobility-shared';
import { WINDOW } from '@innogy/utils-dom';
import { isNotNullish } from '@innogy/utils-rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SetValueAction } from 'ngrx-forms';
import { filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import {
  chargePointColorControlId,
  chargePointFixedCableControlId,
  chargePointTypeControlId,
} from './lead-form-charge-point-configuration.reducer';
import {
  goToExistingCustomerLeadForm,
  initializeLeadForm,
  setEmobilityLeadFormType,
} from './lead-form.actions';
import {
  getPropositionType,
  selectChargePointOptions,
} from './lead-form.selectors';

@Injectable()
export class LeadFormEffects {
  private readonly windowRef = inject(WINDOW);

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<any>
  ) {}
  public readonly prefillChargePointOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emobilityActions.getEmobilityProductsV2.successAction),
      withLatestFrom(this.store$.select(selectChargePointOptions)),
      mergeMap(([_, options]) => {
        const actions: SetValueAction<unknown>[] = [];

        if (options.colors.length === 1) {
          actions.push(
            new SetValueAction(chargePointColorControlId, options.colors.at(0))
          );
        }

        if (options.cableOptions.length === 1) {
          actions.push(
            new SetValueAction(
              chargePointFixedCableControlId,
              options.cableOptions.at(0)
            )
          );
        }

        if (options.stationTypes.length === 1) {
          actions.push(
            new SetValueAction(
              chargePointTypeControlId,
              options.stationTypes.at(0)
            )
          );
        }

        return actions;
      })
    )
  );

  public readonly setLeadFormType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeLeadForm),
      withLatestFrom(
        this.store$.select(getPropositionType).pipe(filter(isNotNullish)),
        (_, proposition) => ({ proposition })
      ),
      map(({ proposition }) => setEmobilityLeadFormType({ proposition }))
    )
  );

  public readonly goToExistingCustomerLeadForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(goToExistingCustomerLeadForm),
        tap(({ url }) => {
          const urlObj = new URL(url, this.windowRef.location.origin);
          const params = new URLSearchParams(this.windowRef.location.search);
          params.forEach((value, key) => {
            urlObj.searchParams.append(key, value);
          });
          this.windowRef.location.assign(urlObj);
        })
      ),
    { dispatch: false }
  );
}
